import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { User } from '@supabase/supabase-js';
import { Globe, Lock, Unlock, Plus, LogOut, User as UserIcon, Clock, RefreshCw, Trash2, Share2, ChevronLeft, Star, ArrowUp, ArrowDown, Shirt, Crown, BookOpen, Terminal, Store } from 'lucide-react';
import { publishAsExperience, unpublishExperience, EXPERIENCE_TYPES, type ExperienceType } from '../services/experienceService';
import GathrBot from '../components/GathrBot';
import CreateWorldModal from '../components/CreateWorldModal';
import { ThemeToggle } from '../components/ThemeToggle';
import './Dashboard.css';
import { URLShortenerService } from '../services/urlShortener';
import { useWorldPresence } from '../hooks/useWorldPresence';
import PrivateWorldGate from '../components/PrivateWorldGate';

interface World {
  id: string;
  name: string;
  thumbnail_url?: string;
  owner_id: string;
  settings: {
    is_public: boolean;
    max_users: number;
    access_code?: string;
    is_locked?: boolean;
  };
  created_at: string;
  updated_at: string;
  owner?: {
    username: string;
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'my-worlds' | 'public-worlds' | 'featured'>('my-worlds');
  const [myWorlds, setMyWorlds] = useState<World[]>([]);
  const [publicWorlds, setPublicWorlds] = useState<World[]>([]);
  // Home-page curation (admins only): featured worlds in display order.
  const [isAdmin, setIsAdmin] = useState(false);
  const [featuredWorlds, setFeaturedWorlds] = useState<World[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // ?join=1 (the TV dashboard's "Join Private") or ?code=XXXXXX opens the join prompt.
  const [showJoinModal, setShowJoinModal] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.has('join') || sp.has('code');
  });
  const [joinCode] = useState(() => new URLSearchParams(window.location.search).get('code') || '');
  const [waitlisted, setWaitlisted] = useState(false);
  const [council, setCouncil] = useState<{ tier: string; seat_number: number } | null>(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('gathrxr.sidebar') === 'collapsed'; } catch { return false; }
  });

  // Subscribe to presence for all visible worlds
  const allWorldIds = useMemo(() => {
    const worlds = activeTab === 'my-worlds' ? myWorlds : activeTab === 'featured' ? featuredWorlds : publicWorlds;
    return worlds.map((w) => w.id);
  }, [myWorlds, publicWorlds, featuredWorlds, activeTab]);
  const worldPresence = useWorldPresence(allWorldIds);

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (user) {
      loadWorlds();
    }
  }, [user, activeTab]);

  // Founders beta: claim a seat (idempotent); if the beta is full, the user
  // lands on the waitlist and AI features stay locked server-side.
  useEffect(() => {
    if (!user) return;
    supabase.rpc('claim_founder_seat').then(({ data, error }) => {
      if (error || !data) return; // migration not applied yet — no gate
      if (data.founder === false && data.waitlisted) {
        setWaitlisted(true);
        if (!sessionStorage.getItem('gathrxr.waitlistShown')) {
          sessionStorage.setItem('gathrxr.waitlistShown', '1');
          setShowWaitlistModal(true);
        }
      }
    });
  }, [user]);

  // Creator Council: link a paid membership to this account (idempotent).
  // Graceful degradation identical to claim_founder_seat: no gate if RPC missing.
  useEffect(() => {
    if (!user) return;
    supabase.rpc('claim_council_membership').then(({ data, error }) => {
      if (error || !data) return; // migration not applied yet — no chip
      setCouncil(data as { tier: string; seat_number: number });
    });
  }, [user]);

  // Platform admins curate the home page's Featured Worlds row.
  useEffect(() => {
    if (!user) return;
    supabase.rpc('is_app_admin').then(({ data, error }) => {
      if (error || data !== true) return; // migration not applied, or not an admin
      setIsAdmin(true);
      loadFeatured();
    });
  }, [user]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  };

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/');
      return;
    }

    // Ensure user has a profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .single();

    if (!profile) {
      // Create profile if it doesn't exist
      await supabase
        .from('profiles')
        .insert({
          id: user.id,
          username: user.email?.split('@')[0] || `user_${user.id.substring(0, 8)}`
        });
    }

    setUser(user);
  };

  const loadFeatured = async () => {
    const { data, error } = await supabase
      .from('featured_worlds')
      .select(`
        rank,
        world:worlds (
          *,
          owner:profiles!worlds_owner_id_fkey(username)
        )
      `)
      .order('rank', { ascending: true });
    if (error) throw error;
    setFeaturedWorlds((data as any[]).map((row) => row.world).filter(Boolean));
  };

  const toggleFeatured = async (world: World) => {
    const isFeatured = featuredWorlds.some((w) => w.id === world.id);
    if (!isFeatured && !world.settings.is_public) {
      showToast('Make the world public before featuring it.');
      return;
    }
    const { error } = await supabase.rpc('set_world_featured', {
      p_world_id: world.id,
      p_featured: !isFeatured,
    });
    if (error) {
      console.error('Error updating featured worlds:', error);
      showToast('Couldn\'t update featured worlds.');
      return;
    }
    await loadFeatured();
    showToast(isFeatured ? `Removed "${world.name}" from the home page.` : `"${world.name}" is now featured on the home page.`);
  };

  const moveFeatured = async (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= featuredWorlds.length) return;
    const next = [...featuredWorlds];
    [next[index], next[target]] = [next[target], next[index]];
    setFeaturedWorlds(next);
    const { error } = await supabase.rpc('set_featured_order', { p_world_ids: next.map((w) => w.id) });
    if (error) {
      console.error('Error reordering featured worlds:', error);
      showToast('Couldn\'t save the new order.');
      loadFeatured();
    }
  };

  const loadWorlds = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (activeTab === 'featured') {
        await loadFeatured();
      } else if (activeTab === 'my-worlds') {
        const { data, error } = await supabase
          .from('worlds')
          .select('*')
          .eq('owner_id', user!.id)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setMyWorlds(data || []);
      } else {
        const { data, error } = await supabase
          .from('worlds')
          .select(`
            *,
            owner:profiles!worlds_owner_id_fkey(username)
          `)
          .eq('settings->is_public', true)
          .neq('owner_id', user!.id)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setPublicWorlds(data || []);
      }
    } catch (error) {
      console.error('Error loading worlds:', error);
      setLoadError('We couldn\'t load your worlds. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const [knockingWorldId, setKnockingWorldId] = useState<string | null>(null);
  const [knockStatus, setKnockStatus] = useState<'idle' | 'waiting' | 'denied'>('idle');
  const [publishModal, setPublishModal] = useState<World | null>(null);
  const [publishType, setPublishType] = useState<ExperienceType>('campfire');

  const doPublishExperience = async () => {
    if (!publishModal) return;
    try {
      await publishAsExperience(publishModal.id, publishType);
      showToast('Published to the Experience Gallery');
      setPublishModal(null);
      loadWorlds();
    } catch (e: any) {
      showToast(e?.message || 'Could not publish');
    }
  };

  const removeExperience = async (world: World) => {
    try {
      await unpublishExperience(world.id);
      showToast('Removed from Experiences');
      loadWorlds();
    } catch (e: any) {
      showToast(e?.message || 'Could not update');
    }
  };

  // Enter world as viewer → lean /vr/ route (no editor)
  const enterWorld = (world: World) => {
    const isOwner = world.owner_id === user?.id;

    // Locked world — only owner can enter, others must knock
    if (world.settings.is_locked && !isOwner) {
      setKnockingWorldId(world.id);
      setKnockStatus('idle');
      return;
    }

    // Private worlds only show up here for their owner and members (RLS), and
    // members are already in, so there's no code to ask for.
    navigate(`/vr/${world.id}?name=${encodeURIComponent(world.name)}`);
  };

  // Open world in full editor → only for owners
  const buildWorld = (world: World) => {
    navigate(`/world/${world.id}?name=${encodeURIComponent(world.name)}`);
  };

  const sendKnock = async (worldId: string) => {
    setKnockStatus('waiting');
    // Send a knock via Supabase Realtime broadcast
    const channel = supabase.channel(`world:${worldId}`);
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.send({
          type: 'broadcast',
          event: 'knock:request',
          payload: {
            userId: user?.id,
            userName: user?.email?.split('@')[0] || 'Someone',
            timestamp: Date.now(),
          },
        });

        // Listen for response
        channel.on('broadcast', { event: 'knock:response' }, (msg: any) => {
          const data = msg.payload;
          if (data?.targetUserId === user?.id) {
            if (data.approved) {
              setKnockingWorldId(null);
              setKnockStatus('idle');
              const world = [...myWorlds, ...publicWorlds].find(w => w.id === worldId);
              navigate(`/vr/${worldId}?name=${encodeURIComponent(world?.name || 'World')}`);
            } else {
              setKnockStatus('denied');
            }
            supabase.removeChannel(channel);
          }
        });
      }
    });

    // Auto-timeout after 30 seconds
    setTimeout(() => {
      setKnockStatus((prev) => {
        if (prev === 'waiting') {
          supabase.removeChannel(channel);
          return 'denied';
        }
        return prev;
      });
    }, 30000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 30) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const toggleWorldPrivacy = async (world: World) => {
    try {
      const newIsPublic = !world.settings.is_public;
      let accessCode = world.settings.access_code;

      // Generate access code if making private and doesn't have one
      if (!newIsPublic && !accessCode) {
        accessCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      }

      const { error } = await supabase
        .from('worlds')
        .update({
          settings: {
            ...world.settings,
            is_public: newIsPublic,
            access_code: newIsPublic ? null : accessCode
          }
        })
        .eq('id', world.id);

      if (error) throw error;

      // Refresh worlds list
      loadWorlds();
    } catch (error) {
      console.error('Error toggling privacy:', error);
      showToast('We couldn\'t update world privacy. Please try again.');
    }
  };

  const toggleWorldLock = async (world: World) => {
    try {
      const newLocked = !world.settings.is_locked;
      const { error } = await supabase
        .from('worlds')
        .update({
          settings: { ...world.settings, is_locked: newLocked }
        })
        .eq('id', world.id);
      if (error) throw error;
      loadWorlds();
    } catch (error) {
      console.error('Error toggling lock:', error);
      showToast('We couldn\'t update the world lock. Please try again.');
    }
  };

  const deleteWorld = async (world: World) => {
    const isTemplate = !!(world.settings as any)?.is_template;
    const msg = isTemplate
      ? `"${world.name}" is a published Experience. Deleting it removes it from the Experiences gallery for everyone, and this can't be undone. Delete anyway?`
      : 'Are you sure you want to delete this world? This action cannot be undone.';
    if (!window.confirm(msg)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('worlds')
        .delete()
        .eq('id', world.id);

      if (error) throw error;

      // Refresh worlds list
      loadWorlds();
    } catch (error) {
      console.error('Error deleting world:', error);
      showToast('We couldn\'t delete the world. Please try again.');
    }
  };

  const shareWorld = async (world: World) => {
    const originalURL = `${window.location.origin}/world/${world.id}?name=${encodeURIComponent(world.name)}`;
    const shortURL = await URLShortenerService.getShortenedURL(originalURL, world.name);

    // Copy to clipboard
    navigator.clipboard.writeText(shortURL).then(() => {
      showToast('Share link copied to clipboard');
    }).catch((error) => {
      console.error('Failed to copy to clipboard:', error);
      // Fallback: show the URL in a prompt so user can copy manually
      prompt('Copy this share link:', shortURL);
    });
  };

  // The join itself happens in PrivateWorldGate (join_world_with_code RPC).
  const onJoinedPrivateWorld = (worldId: string) => {
    setShowJoinModal(false);
    navigate(`/vr/${worldId}`);
  };

  const OnlineIndicator: React.FC<{ worldId: string }> = ({ worldId }) => {
    const info = worldPresence[worldId];
    const count = info?.count || 0;
    const players = info?.players || [];

    const nameColor = (name: string) => {
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return `hsl(${Math.abs(hash) % 360}, 60%, 50%)`;
    };
    const getInitials = (name: string) => {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    };

    const maxShow = 4;
    const visible = players.slice(0, maxShow);
    const overflow = count - maxShow;
    const isActive = count > 0;

    return (
      <div className={`online-indicator ${isActive ? 'active' : ''}`}>
        {/* The count is only shown when there is something to count. An empty
            world previously read "● 0 … no one online" — the same fact stated
            twice, on a card where every other line carries new information. */}
        {isActive && (
          <div className="online-count">
            <span className="online-dot" />
            {count}
          </div>
        )}
        {visible.length > 0 && (
          <div className="online-avatars">
            {visible.map((p, i) => (
              <div
                key={i}
                title={p.userName}
                className="online-avatar"
                style={{ background: nameColor(p.userName), zIndex: count - i }}
              >
                {getInitials(p.userName)}
              </div>
            ))}
            {overflow > 0 && (
              <div className="online-avatar overflow">+{overflow}</div>
            )}
          </div>
        )}
        <span className="online-label">
          {isActive ? 'online now' : 'Empty'}
        </span>
      </div>
    );
  };

  const renderWorldCard = (world: World, index: number) => {
    const isOwner = world.owner_id === user?.id;
    const isFeatured = featuredWorlds.some((w) => w.id === world.id);
    return (
      <div key={world.id} className="world-card">
        <div className="world-thumbnail" onClick={() => enterWorld(world)}>
          <img
            src={world.thumbnail_url || '/gathrxr-hero.webp'}
            alt={world.name}
            className={world.thumbnail_url ? undefined : 'thumbnail-fallback'}
            loading="lazy"
          />
          <div className="world-overlay">
            {isOwner && (
              <button
                className="btn-overlay btn-build"
                onClick={(e) => { e.stopPropagation(); buildWorld(world); }}
              >
                Build
              </button>
            )}
            <button
              className="btn-overlay btn-enter"
              onClick={(e) => { e.stopPropagation(); enterWorld(world); }}
            >
              Enter
            </button>
          </div>
        </div>
        <div className="world-info">
          <div className="world-card-header">
            <h3>{world.name}</h3>
            {isAdmin && (
              <div className="world-actions admin-actions">
                {activeTab === 'featured' && (
                  <>
                    <button
                      className="action-btn"
                      onClick={(e) => { e.stopPropagation(); moveFeatured(index, -1); }}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      className="action-btn"
                      onClick={(e) => { e.stopPropagation(); moveFeatured(index, 1); }}
                      disabled={index === featuredWorlds.length - 1}
                      title="Move down"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </>
                )}
                <button
                  className={`action-btn ${isFeatured ? 'featured' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleFeatured(world); }}
                  title={isFeatured ? 'Remove from home page' : 'Feature on home page'}
                  aria-pressed={isFeatured}
                >
                  <Star size={16} fill={isFeatured ? 'currentColor' : 'none'} />
                </button>
              </div>
            )}
            {isOwner && (
              <div className="world-actions">
                <button
                  className="action-btn accent"
                  onClick={(e) => { e.stopPropagation(); shareWorld(world); }}
                  title="Share World"
                >
                  <Share2 size={16} />
                </button>
                <button
                  className={`action-btn ${(world.settings as any).is_template ? 'accent' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if ((world.settings as any).is_template) removeExperience(world);
                    else setPublishModal(world);
                  }}
                  title={(world.settings as any).is_template ? 'Published as Experience — click to remove' : 'Publish as Experience'}
                >
                  <GathrBot size={16} />
                </button>
                <button
                  className={`action-btn ${world.settings.is_locked ? 'locked' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleWorldLock(world); }}
                  title={world.settings.is_locked ? 'Unlock World (allow entry)' : 'Lock World (block entry)'}
                >
                  {world.settings.is_locked ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
                <button
                  className="action-btn"
                  onClick={(e) => { e.stopPropagation(); toggleWorldPrivacy(world); }}
                  title={world.settings.is_public ? 'Make Private' : 'Make Public'}
                >
                  {world.settings.is_public ? <Globe size={16} /> : <Lock size={16} />}
                </button>
                <button
                  className="action-btn delete"
                  onClick={(e) => { e.stopPropagation(); deleteWorld(world); }}
                  title="Delete World"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
          <div className="world-meta">
            {!isOwner && (
              <span>
                <UserIcon size={14} />
                {world.owner?.username || 'Unknown'}
              </span>
            )}
            {isOwner && (
              <span className="privacy-badge">
                {world.settings.is_public ? <><Globe size={14} />Public</> : <><Lock size={14} />Private</>}
              </span>
            )}
            {world.settings.is_locked && (
              <span className="locked-badge">
                <Lock size={12} />
                Locked
              </span>
            )}
            <span>
              <Clock size={14} />
              {formatDate(world.updated_at)}
            </span>
          </div>
          {isOwner && !world.settings.is_public && world.settings.access_code && (
            <div className="access-code">
              Access code: <code>{world.settings.access_code}</code>
            </div>
          )}
          <OnlineIndicator worldId={world.id} />
        </div>
      </div>
    );
  };

  const worlds = activeTab === 'my-worlds' ? myWorlds : activeTab === 'featured' ? featuredWorlds : publicWorlds;

  return (
    <div className="dashboard-container">
      {/* Left Sidebar */}
      <div className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <button
          className="sidebar-toggle"
          onClick={() => {
            const next = !sidebarCollapsed;
            setSidebarCollapsed(next);
            try { localStorage.setItem('gathrxr.sidebar', next ? 'collapsed' : 'expanded'); } catch {}
          }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="sidebar-header">
          <div className="sidebar-header-row">
            <h2><span className="logo-full">GathrXR</span><span className="logo-icon">G</span></h2>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">Worlds</div>
          <div className="nav-item-wrapper">
            <button
              className={`nav-item ${activeTab === 'my-worlds' ? 'active' : ''}`}
              onClick={() => setActiveTab('my-worlds')}
            >
              <UserIcon size={18} />
              <span>My Worlds</span>
            </button>
            <div className="nav-tooltip">My Worlds</div>
          </div>

          <div className="nav-item-wrapper">
            <button
              className={`nav-item ${activeTab === 'public-worlds' ? 'active' : ''}`}
              onClick={() => setActiveTab('public-worlds')}
            >
              <Globe size={18} />
              <span>Public Worlds</span>
            </button>
            <div className="nav-tooltip">Public Worlds</div>
          </div>

          {isAdmin && (
            <div className="nav-item-wrapper">
              <button
                className={`nav-item ${activeTab === 'featured' ? 'active' : ''}`}
                onClick={() => setActiveTab('featured')}
              >
                <Star size={18} />
                <span>Featured</span>
              </button>
              <div className="nav-tooltip">Featured on home page</div>
            </div>
          )}

          <div className="nav-item-wrapper">
            <button
              className="nav-item"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} />
              <span>Create World</span>
            </button>
            <div className="nav-tooltip">Create World</div>
          </div>

          <div className="nav-item-wrapper">
            <button
              className="nav-item"
              onClick={() => setShowJoinModal(true)}
            >
              <Lock size={18} />
              <span>Join Private</span>
            </button>
            <div className="nav-tooltip">Join Private</div>
          </div>

          <div className="nav-section">Create</div>
          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/marketplace')}>
              <Store size={18} />
              <span>Marketplace</span>
            </button>
            <div className="nav-tooltip">Marketplace</div>
          </div>

          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/experiences')}>
              <GathrBot size={19} />
              <span>Experiences</span>
            </button>
            <div className="nav-tooltip">Experiences</div>
          </div>

          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/avatars')}>
              <Shirt size={18} />
              <span>Avatars</span>
            </button>
            <div className="nav-tooltip">Avatars</div>
          </div>

          <div className="nav-section">Build</div>
          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/developer')}>
              <Terminal size={18} />
              <span>Developer</span>
            </button>
            <div className="nav-tooltip">Developer / MCP</div>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="nav-item-wrapper">
            <button
              className="nav-item"
              onClick={() => navigate('/council')}
              title="Creator Council — our limited founding circle"
              style={{ color: '#ffd166' }}
            >
              <Crown size={18} />
              <span>Creator Council</span>
              {council && (
                <span
                  title={`Council member — seat #${council.seat_number}`}
                  style={{
                    marginLeft: 'auto',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    color: '#060e18',
                    background: '#ffd166',
                    borderRadius: 999,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                    // The badge holds its size; the label is what gives way.
                    // Both shrinking is how "Creator Council" became
                    // "Creator …" beside "Council · Vi…".
                    flexShrink: 0,
                  }}
                >
                  {council.tier.charAt(0).toUpperCase() + council.tier.slice(1)}
                </span>
              )}
            </button>
            <div className="nav-tooltip">Creator Council</div>
          </div>
          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/docs')} title="How to use GathrXR">
              <BookOpen size={18} />
              <span>Docs</span>
            </button>
            <div className="nav-tooltip">Docs &amp; Guides</div>
          </div>
          <div className="nav-item-wrapper">
            <button
              className="nav-item"
              onClick={() => navigate('/donate')}
              title="Help us maintain a free plan during Beta (or perhaps longer)"
            >
              <span style={{ fontSize: 16 }}>☕</span>
              <span>Support</span>
            </button>
            <div className="nav-tooltip">Help keep the free plan alive</div>
          </div>
          <div className="nav-item-wrapper sidebar-theme-toggle">
            <ThemeToggle />
          </div>
          <div className="nav-section">Account</div>
          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={() => navigate('/profile')}>
              <UserIcon size={18} />
              <span>Profile</span>
            </button>
            <div className="nav-tooltip">Profile</div>
          </div>
          <div className="nav-item-wrapper">
            <button className="nav-item" onClick={handleSignOut}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
            <div className="nav-tooltip">Sign Out</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h1>{activeTab === 'my-worlds' ? 'My Worlds' : activeTab === 'featured' ? 'Featured on Home Page' : 'Public Worlds'}</h1>
          <div className="dashboard-header-actions">
            <button
              className="btn-create btn-secondary"
              onClick={() => loadWorlds()}
              title="Refresh worlds"
            >
              <RefreshCw size={20} />
              Refresh
            </button>
            {activeTab === 'my-worlds' && (
              <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                <Plus size={20} />
                Create New World
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="worlds-grid" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="world-card skeleton-card">
                <div className="skeleton-thumb" />
                <div className="skeleton-body">
                  <div className="skeleton-line" style={{ width: '60%' }} />
                  <div className="skeleton-line" style={{ width: '40%' }} />
                  <div className="skeleton-line" style={{ width: '80%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="error-state">
            <Globe size={48} />
            <h3>Couldn't load worlds</h3>
            <p>{loadError}</p>
            <button className="btn-create" onClick={() => loadWorlds()}>
              <RefreshCw size={18} />
              Try again
            </button>
          </div>
        ) : (
          <div className="worlds-grid">
            {worlds.length === 0 ? (
              activeTab === 'my-worlds' ? (
                <div className="empty-state">
                  <Globe size={64} />
                  <h3>No worlds yet</h3>
                  <p>Create your first world to get started.</p>
                  <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                    <Plus size={20} />
                    Create World
                  </button>
                </div>
              ) : activeTab === 'featured' ? (
                <div className="empty-state">
                  <Star size={64} />
                  <h3>Nothing featured yet</h3>
                  <p>Star a public world to put it on the home page.</p>
                </div>
              ) : (
                <div className="empty-state">
                  <Globe size={64} />
                  <h3>No public worlds yet</h3>
                  <p>Be the first to share your world.</p>
                </div>
              )
            ) : (
              worlds.map((world, i) => renderWorldCard(world, i))
            )}
          </div>
        )}
      </div>

      <CreateWorldModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Join Private World Modal */}
      {showJoinModal && user && (
        <PrivateWorldGate
          signedIn={!!user}
          initialCode={joinCode}
          returnTo="/dashboard/classic?join=1"
          onJoined={onJoinedPrivateWorld}
          onCancel={() => setShowJoinModal(false)}
        />
      )}

      {publishModal && (
        <div className="gx-modal-overlay" onClick={() => setPublishModal(null)}>
          <div className="gx-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Publish as Experience</h2>
            <p>“{publishModal.name}” will appear in the Experience Gallery for anyone to clone into their own world. Pick the kind of experience it is.</p>
            <div className="xp-type-grid">
              {EXPERIENCE_TYPES.filter((t) => t.type !== 'other').map((t) => (
                <button
                  key={t.type}
                  className={`xp-type-option ${publishType === t.type ? 'selected' : ''}`}
                  onClick={() => setPublishType(t.type)}
                >
                  <span className="xp-type-label">{t.label}</span>
                  <span className="xp-type-blurb">{t.blurb}</span>
                </button>
              ))}
            </div>
            <div className="gx-modal-actions">
              <button className="gx-btn-primary" onClick={doPublishExperience}>Publish</button>
              <button className="gx-btn-ghost" onClick={() => setPublishModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Knock Modal */}
      {knockingWorldId && (
        <div className="gx-modal-overlay">
          <div className="gx-modal centered">
            <Lock size={48} className="gx-modal-icon" />
            <h2>World Locked</h2>
            <p>
              {knockStatus === 'idle' && 'This world is locked by the creator. You can knock to request entry.'}
              {knockStatus === 'waiting' && 'Knock sent. Waiting for the owner to respond...'}
              {knockStatus === 'denied' && 'Your request was not accepted. The owner may be busy or away.'}
            </p>

            {knockStatus === 'waiting' && (
              <div className="gx-modal-spinner">
                <div className="loading-spinner small" />
              </div>
            )}

            <div className="gx-modal-actions">
              {knockStatus === 'idle' && (
                <button className="gx-btn-primary" onClick={() => sendKnock(knockingWorldId)}>
                  Knock
                </button>
              )}
              <button
                className="gx-btn-ghost"
                onClick={() => { setKnockingWorldId(null); setKnockStatus('idle'); }}
              >
                {knockStatus === 'denied' ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waitlist modal */}
      {showWaitlistModal && (
        <div className="gx-modal-overlay" onClick={() => setShowWaitlistModal(false)}>
          <div className="gx-modal centered" onClick={(e) => e.stopPropagation()}>
            <h2>You're on the waitlist 🎟</h2>
            <p>
              All founders beta seats are taken. You can still build worlds, use the
              object library, voice, and multiplayer — AI generation unlocks when a
              seat opens. We'll be growing the beta soon!
            </p>
            <div className="gx-modal-actions">
              <button className="gx-btn-primary" onClick={() => setShowWaitlistModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {waitlisted && (
        <div style={{
          position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)',
          background: 'color-mix(in srgb, var(--gx-kick) 18%, var(--gx-bg-2))',
          border: '1px solid color-mix(in srgb, var(--gx-kick) 45%, transparent)',
          color: 'var(--gx-text)', padding: '6px 16px', borderRadius: 999,
          fontSize: 12.5, zIndex: 900,
        }}>
          Beta full — you're on the waitlist. Building is open; AI unlocks when a seat frees up.
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="gx-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
